import type { StatusType } from '@/shared/ui'
import { EventErrorType, type ObjectPinStatuses } from '@/entities/Object'
import { useTranslate } from '@/shared/lib'
import { range } from 'lodash'

/* Список pin для отходящих контакторов */
export const OutputProtectRange = [...range(6, 10), ...range(44, 59), ...range(197, 209)]

/* Список pin для входящих контакторов */
export const ContactorRange = [...range(10, 13), ...range(41, 44), ...range(193, 197)]

/**
 * Функция распределения статусов
 * @param cabinet
 */
export const getPinStatuses = (cabinet: any) => {
  const statuses: StatusType[] = []
  const pinStatuses = cabinet.pin_statuses || []
  let outputProtectCount = 0
  const { trs } = useTranslate()

  pinStatuses.forEach((item: ObjectPinStatuses) => {
    if (item.device_event_type_id === 4 || item.value == -1) return

    switch (item.device_event_type_id) {
      case 1: {
        statuses.push({
          type: 'door',
          event_type: EventErrorType.WARNING,
          icon: item.value == 1 ? 'lock_open' : 'lock',
          error: item.value == 1,
          tooltip: item.value == 1 ? trs('itf.statuses.door_open') : trs('itf.statuses.door_close'),
        })
        break
      }
      case 3: {
        statuses.push({
          type: 'fire',
          event_type: EventErrorType.ERROR,
          icon: item.value == 1 ? 'fire_extinguisher' : 'local_fire_department',
          error: item.value == 0,
          tooltip: item.value == 1 ? trs('itf.statuses.not_fire') : trs('itf.statuses.fire'),
        })
        break
      }
      case 5: {
        statuses.push({
          type: 'input_protect',
          event_type: EventErrorType.ERROR,
          icon: 'shield',
          error: item.value == 1,
          tooltip:
            item.value == 1
              ? trs('itf.statuses.input_control_off')
              : trs('itf.statuses.input_control_on'),
        })
        break
      }
      case 9: {
        statuses.push({
          type: 'remote',
          event_type: EventErrorType.SUCCESS,
          icon: item.value == 1 ? 'nest_remote_comfort_sensor' : 'touch_app',
          error: false,
          tooltip:
            item.value == 1
              ? trs('itf.statuses.control_remote')
              : trs('itf.statuses.control_local'),
        })
        break
      }
      case 18: {
        statuses.push({
          type: 'signal',
          event_type: EventErrorType.SUCCESS,
          icon: 'signal_cellular_2_bar',
          error: false,
          tooltip: trs('itf.statuses.signal', { value: item.value }),
          badge: item.value,
        })
        break
      }
      default: {
        if (OutputProtectRange.includes(item.device_event_type_id) && item.value == 1) {
          outputProtectCount++
        }
      }
    }
  })

  if (outputProtectCount) {
    statuses.push({
      type: 'output_protect',
      event_type: EventErrorType.ERROR,
      icon: 'shield',
      error: true,
      tooltip: trs('itf.statuses.output_control_count', { count: outputProtectCount }),
      text: outputProtectCount,
    })
  }

  return statuses
}

/**
 * Функция распределения основных статусов
 * @param cabinet
 */
export const getMainStatuses = (cabinet: any) => {
  const statuses: StatusType[] = []
  const { trs } = useTranslate()

  for (let item in cabinet.main_statuses) {
    switch (item) {
      case 'long_offline_error':
        if (cabinet.main_statuses[item]) {
          statuses.push({
            type: item,
            event_type: EventErrorType.ERROR,
            icon: 'signal_disconnected',
            error: true,
            tooltip: trs('itf.statuses.long_offline'),
          })
        }
        break
      case 'frequent_reboot':
        if (cabinet.main_statuses[item]) {
          statuses.push({
            type: item,
            event_type: EventErrorType.WARNING,
            icon: 'rotate_auto',
            error: true,
            tooltip: trs('itf.statuses.frequent_reboot'),
          })
        }
        break
      case 'are_settings_synced':
        if (!cabinet.main_statuses[item]) {
          statuses.push({
            type: item,
            event_type: EventErrorType.WARNING,
            icon: 'reset_wrench',
            error: true,
            tooltip: trs('itf.statuses.settings_not_synchro'),
          })
        }
        break
      case 'is_schedule_synced':
        if (!cabinet.main_statuses[item]) {
          statuses.push({
            type: item,
            event_type: EventErrorType.WARNING,
            icon: 'event_busy',
            error: true,
            tooltip: trs('itf.statuses.schedule_not_synchro'),
          })
        }
        break
    }
  }

  return statuses
}

/**
 * Функция распределения статусов счетчиков
 * @param cabinet
 */
export const getMeterStatuses = (cabinet: any) => {
  const statuses: StatusType[] = []
  const meters = cabinet.meters || []
  const { trs } = useTranslate()

  meters.forEach((item: any) => {
    statuses.push({
      type: 'meters',
      event_type: EventErrorType.ERROR,
      icon: 'crop_square',
      error: !item.is_connected,
      tooltip: item.is_connected
        ? trs('itf.meter_is_connect', { serial: item.serial_number })
        : trs('itf.meter_is_not_connect', { serial: item.serial_number }),
      text: 'wh',
    })
  })

  return statuses
}

/**
 * Функция распределения статусов электросетевых параметров
 * @param cabinet
 */
export const getElParamsStatuses = (cabinet: any) => {
  const statuses: StatusType[] = []
  const elParamsGroup: { i: any; u: any; p: any } = { i: {}, u: {}, p: {} }

  const { trs } = useTranslate()

  for (let item in cabinet.errors_el_params) {
    if (!cabinet.errors_el_params[item]) continue

    if (item.includes('_i_')) {
      elParamsGroup.i[item[item.length - 1]] = cabinet.errors_el_params[item]
    } else if (item.includes('_u_')) {
      elParamsGroup.u[item[item.length - 1]] = cabinet.errors_el_params[item]
    } else if (item.includes('_p_')) {
      elParamsGroup.p[item[item.length - 1]] = cabinet.errors_el_params[item]
    }
  }

  /**
   * Функция формирования строки параметров
   * @param values
   * @param param
   */
  const paramsString = (values: string[], param: string): string => {
    let messages: string[] = []

    for (let value in values) {
      const result = Number(values[value]) === 2

      if (value === 'm') {
        messages.push(trs(result ? 'itf.meter_min_p_sum_error' : 'itf.meter_p_sum_error'))
      } else {
        messages.push(
          trs(result ? `itf.meter_min_${param}_error` : `itf.meter_${param}_error`, {
            param: value.toUpperCase(),
          }),
        )
      }
    }

    return messages.join('<br />')
  }

  for (let item in elParamsGroup) {
    if (!Object.keys(elParamsGroup[item]).length) continue

    statuses.push({
      type: 'el_params',
      event_type: EventErrorType.ERROR,
      icon: 'electric_bolt',
      error: true,
      tooltip: paramsString(elParamsGroup[item], item),
      badge: item.toUpperCase(),
    })
  }

  return statuses
}
